import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { Checkbox, CircularProgress } from "@mui/material";
import { styled } from "@mui/material/styles";
import { get, includes } from "lodash";
import { Link } from "react-router-dom";
import MapboxMap from "../../../common/js/components/MapboxMap";
import { Sorty } from "../../../common/js/icons";
import { formatPrice, markerLabelContent } from "../utils/operations";
import { NO_CONTENT_PLACEHOLDER } from "./constants";
import "./Listings.css";
import schema from "./reviewListingsTables";

const StyledAlternateContent = styled("div")({
    margin: "auto",
    textAlign: "center",
    marginTop: "6em",
    marginBottom: "6em",
    color: "rgba(0, 0, 0, 0.30)",
    fontSize: "20px",
});

const StyledLink = styled(Link)({
    textDecoration: "none",
});

const StyledListingLink = styled("span")({
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
    color: "#2C99D2",
    fontWeight: "600",
    display: "-webkit-box",
    WebkitLineClamp: "2",
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
});

const StyledWrapper = styled("div")({
    fontFamily: "IBM Plex Sans, sans-serif",
    marginRight: "10px",
    overflowX: "auto",
    flexGrow: 0,
    maxWidth: "41.666667%",
    flexBasis: "41.666667%",
});

const StyledHeaderRow = styled("div")({
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid rgb(212, 212, 212)",
    color: "#9E9E9E",
    fontSize: "12px",
    fontWeight: "bold",
    padding: "1.5em 0",
    overflow: "hidden",
    scrollbarGutter: "stable",
    flexWrap: "wrap",
    boxSizing: "border-box",
    width: "100%",
});

const StyledBodyRow = styled("div")({
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid rgb(212, 212, 212)",
    fontSize: "13px",
    height: "fit-content",
    padding: "0.75em 0",
    flexWrap: "wrap",
    boxSizing: "border-box",
});

const StyledBodyWrapper = styled("div")({
    display: "block",
    maxHeight: "50vh",
    minHeight: "425px",
    overflowY: "auto",
    scrollbarGutter: "stable",
});

const ListingsWithMap = (props) => {
    const {
        handleCheckboxClick,
        handleDragEnd,
        handleGridSort,
        handleSearchCleared,
        handleSelectAll,
        handleSelectAllGroup,
        listings,
        listingTab,
        loadingSearch,
        mapCrumbCleared,
        mapSearchSettings,
        savedMapDetails,
        saveMapSearchSettings,
        searchCleared,
        searchPolygon,
        searchResponse,
        searchRun,
        selectAll,
        selectAllGroup,
        selectedListings,
        selectedListingsGroup,
        updateSearchAreaFilter,
        updateSearchPolygon,
        clearFilters,
        savedSearchRun,
    } = props;

    const listingsInListingTab = get(listings, listingTab, []);
    const hasListings = Object.keys(listingsInListingTab).length > 0;
    const disableDraw = listingTab === "listings_group" ? true : false;
    const headers = schema.viewHeaders["all_km_listings_with_map"];
    const loadRows = !loadingSearch && !searchCleared && hasListings;

    const onDragEnd = (results) => {
        const { destination, source } = results;
        if (!destination) return;
        if (destination.droppableId === source.droppableId && destination.index === source.index) return;
        handleDragEnd(results, listingsInListingTab, listingTab);
    };

    return (
        <div style={{ display: "flex", flexWrap: "wrap", boxSizing: "border-box", width: "100%" }}>
            <StyledWrapper>
                <div style={{ minWidth: "500px" }}>
                    <StyledHeaderRow>
                        {headers.map((header, i) => {
                            return (
                                <div
                                    key={`gh-c${i}`}
                                    numeric={header.numeric}
                                    onClick={() => handleGridSort(header.name, listingTab)}
                                    style={{
                                        paddingRight: "7px",
                                        flexGrow: 0,
                                        flexBasis:
                                            header.name === "Address"
                                                ? "33.33%"
                                                : header.name === "SQFT"
                                                  ? "16.67%"
                                                  : "25%",
                                        maxWidth:
                                            header.name === "Address"
                                                ? "33.33%"
                                                : header.name === "SQFT"
                                                  ? "16.67%"
                                                  : "25%",
                                        boxSizing: "border-box",
                                    }}
                                >
                                    {header.name === "Property" ? (
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            {listingTab === "listings_group" ? (
                                                <Checkbox
                                                    className={
                                                        selectAllGroup
                                                            ? "listingCheckboxAllSelected"
                                                            : "listingCheckboxAll"
                                                    }
                                                    checked={selectAllGroup}
                                                    value={selectAllGroup}
                                                    onChange={() => handleSelectAllGroup()}
                                                    color="primary"
                                                />
                                            ) : (
                                                <Checkbox
                                                    className={
                                                        selectAll ? "listingCheckboxAllSelected" : "listingCheckboxAll"
                                                    }
                                                    checked={selectAll}
                                                    value={selectAll}
                                                    onChange={() => handleSelectAll()}
                                                    color="primary"
                                                />
                                            )}
                                            <span style={{ paddingLeft: "7px" }}>{header.name.toUpperCase()}</span>
                                        </div>
                                    ) : (
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            <span style={{ paddingRight: "7px" }}>{header.name.toUpperCase()}</span>
                                            <Sorty />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </StyledHeaderRow>

                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="droppable" style={{ maxWidth: "100%" }}>
                            {(provided) => (
                                <StyledBodyWrapper {...provided.droppableProps} ref={provided.innerRef}>
                                    {loadRows
                                        ? listingsInListingTab.map((listing, i) =>
                                              row(
                                                  listing,
                                                  i,
                                                  headers,
                                                  listingTab,
                                                  handleCheckboxClick,
                                                  selectedListings,
                                                  selectedListingsGroup
                                              )
                                          )
                                        : alternateContent(listingTab, loadingSearch, hasListings, searchRun)}

                                    {provided.placeholder}
                                </StyledBodyWrapper>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>
            </StyledWrapper>

            <div style={{ flexBasis: "auto", flexGrow: 1, marginTop: "10px", maxWidth: "58.333333%" }}>
                <MapboxMap
                    disableDraw={disableDraw}
                    places={
                        listingTab === "all_km_listings"
                            ? (searchResponse?.results ?? [])
                            : (listingsInListingTab ?? [])
                    }
                    requestId={searchResponse?.requestId ?? null}
                    savedMapDetails={savedMapDetails}
                    searchCleared={searchCleared}
                    markerLabelContentFunc={markerLabelContent}
                    searchByArea={updateSearchAreaFilter}
                    handleSearchCleared={handleSearchCleared}
                    savedPolygon={searchPolygon}
                    updateSearchPolygon={updateSearchPolygon}
                    saveMapSearchSettings={saveMapSearchSettings}
                    mapSearchSettings={mapSearchSettings}
                    isListing={true}
                    clearFilters={clearFilters}
                    mapCrumbCleared={mapCrumbCleared}
                    savedSearchRun={savedSearchRun}
                />
            </div>
        </div>
    );
};

const row = (listing, i, headers, listingTab, handleCheckboxClick, selectedListings, selectedListingsGroup) => {
    const getItemStyle = (isDragging, draggableStyle) => ({
        background: isDragging ? "#C5C5C5" : "white",
        userSelect: "none",
        width: "100%",
        ...draggableStyle,
    });

    return (
        <Draggable
            draggableId={`AllListingsWMap Draggable - ${String(i)}`}
            index={i}
            style={{ width: "100%", height: "fit-content" }}
            key={`gb-r${i}`}
        >
            {(provided, snapshot) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                >
                    <StyledBodyRow>
                        {headers.map((header, j) => {
                            const propertyPhoto =
                                get(listing, "listing_photo", null) !== null
                                    ? get(listing, "listing_photo", null)
                                    : get(listing, "propertyPhotoUrl", null);
                            const isKmManaged = get(listing, "property.kmManagedFlg", false);

                            return (
                                <div
                                    key={`gb-r${i}-c${j}`}
                                    style={{
                                        boxSizing: "border-box",
                                        paddingRight: "7px",
                                        flexGrow: 0,
                                        flexBasis:
                                            header.name === "Address"
                                                ? "33.33%"
                                                : header.name === "SQFT"
                                                  ? "16.67%"
                                                  : "25%",
                                        maxWidth:
                                            header.name === "Address"
                                                ? "33.33%"
                                                : header.name === "SQFT"
                                                  ? "16.67%"
                                                  : "25%",
                                    }}
                                >
                                    {
                                        /* --- Property --- */
                                        header.name === "Property" ? (
                                            <div style={{ display: "flex", alignItems: "center" }}>
                                                {listingTab === "listings_group" ? (
                                                    <Checkbox
                                                        className={
                                                            selectedListingsGroup.get(listing.listingKey) === true
                                                                ? "listingCheckboxAllSelected"
                                                                : "listingCheckboxAll"
                                                        }
                                                        checked={selectedListingsGroup.get(listing.listingKey) === true}
                                                        value={selectedListingsGroup.get(listing.listingKey) === true}
                                                        onChange={() =>
                                                            handleCheckboxClick(listing.listingKey, listingTab)
                                                        }
                                                        color="primary"
                                                    />
                                                ) : (
                                                    <Checkbox
                                                        className={
                                                            selectedListings.get(listing.listingKey) === true
                                                                ? "listingCheckboxAllSelected"
                                                                : "listingCheckboxAll"
                                                        }
                                                        checked={selectedListings.get(listing.listingKey) === true}
                                                        value={selectedListings.get(listing.listingKey) === true}
                                                        onChange={() =>
                                                            handleCheckboxClick(listing.listingKey, listingTab)
                                                        }
                                                        color="primary"
                                                    />
                                                )}
                                                <div
                                                    className="propertySmallImageContainer"
                                                    style={{
                                                        backgroundImage: `url(${propertyPhoto})`,
                                                        marginLeft: "7px",
                                                        width: "85px",
                                                        minWidth: "85px",
                                                        height: "85px",
                                                    }}
                                                >
                                                    {isKmManaged && <span className="listingTag">KM Managed</span>}
                                                </div>
                                            </div>
                                        ) : /* --- Address --- */
                                        header.name === "Address" ? (
                                            <div>
                                                <StyledLink to={`/listing/${listing.listingKey}`} target="_blank">
                                                    <StyledListingLink
                                                        style={
                                                            listing.expired ? { color: "red" } : { color: "#2C99D2" }
                                                        }
                                                        data-testid={`listingSearchResult-${i}`}
                                                    >
                                                        {get(listing, "listingName").trim()}
                                                    </StyledListingLink>
                                                </StyledLink>
                                                <div>{`${get(listing, "property.primaryAddress.address1", {})}`}</div>
                                                <div>{`${get(listing, "property.primaryAddress.city.city", {})}, ${get(
                                                    listing,
                                                    "property.primaryAddress.city.state.stateCode",
                                                    {}
                                                )}, ${get(
                                                    listing,
                                                    "property.primaryAddress.zipCode.zipCode",
                                                    {}
                                                )}`}</div>
                                            </div>
                                        ) : /* --- Rate / Price --- */
                                        header.name === "Rate/Price" ? (
                                            <div numeric={header.numeric}>
                                                {(get(listing, header.prop) === null ||
                                                    get(listing, header.prop) === 0) &&
                                                (get(listing, "listPrice", null) === null ||
                                                    get(listing, "listPrice", null) === 0) ? (
                                                    "Call Broker"
                                                ) : get(listing, "listingType").toLowerCase() === "lease" ? (
                                                    includes(
                                                        ["Office", "Retail", "Healthcare", "Life Science"],
                                                        get(listing, "useType")
                                                    ) &&
                                                    (includes(
                                                        ["WA", "OR", "AZ"],
                                                        get(listing, "property.primaryAddress.city.state.stateCode")
                                                    ) ||
                                                        get(listing, "property.primaryAddress.city.city", {}) ===
                                                            "San Francisco") ? (
                                                        `$${get(listing, "baseRentPerYear").toFixed(2)} /SF/YR`
                                                    ) : (
                                                        `$${get(listing, header.prop).toFixed(2)} /SF/MTH`
                                                    )
                                                ) : (
                                                    <div>{`$${
                                                        get(listing, "listPrice", null) !== null
                                                            ? formatPrice(get(listing, "listPrice", 0))
                                                            : NO_CONTENT_PLACEHOLDER
                                                    }`}</div>
                                                )}
                                            </div>
                                        ) : /* --- Square Feet --- */
                                        header.name === "SQFT" ? (
                                            <div numeric={header.numeric}>
                                                {get(listing, header.prop) !== null
                                                    ? header.transform(get(listing, header.prop))
                                                    : NO_CONTENT_PLACEHOLDER}
                                            </div>
                                        ) : null
                                    }
                                </div>
                            );
                        })}
                    </StyledBodyRow>
                </div>
            )}
        </Draggable>
    );
};

const alternateContent = (listingTab, loadingSearch, hasListings, searchRun) => {
    return (
        <div>
            {listingTab === "all_km_listings" &&
                (loadingSearch ? (
                    <StyledAlternateContent>
                        <CircularProgress color="primary" />
                    </StyledAlternateContent>
                ) : !hasListings ? (
                    searchRun ? (
                        <StyledAlternateContent>No Listings</StyledAlternateContent>
                    ) : (
                        <StyledAlternateContent>Please apply a search criteria</StyledAlternateContent>
                    )
                ) : null)}

            {listingTab === "listings_group" &&
                (loadingSearch ? (
                    <StyledAlternateContent>
                        <CircularProgress color="primary" />
                    </StyledAlternateContent>
                ) : !hasListings ? (
                    <StyledAlternateContent>No Listings</StyledAlternateContent>
                ) : null)}
        </div>
    );
};

export default ListingsWithMap;
